import { Stack } from "@mantine/core";
import type { ComponentProps, ComponentType, PropsWithChildren } from "react";
import type { Permission } from "src/api/Tmd.dtos";
import type { ILayoutItemContext, LayoutPermissionMode } from "src/contexts/layouts/LayoutContext";
import SetPageLoading from "../SetPageLoading";
import SetPagePermission from "../SetPagePermission";

export enum PageComponentPosition {
    BeforeChildren = 1,
    AfterChildren = 2,
}

export type PageProps<TComponent extends ComponentType<any>> = PropsWithChildren<{}> & {
    loading: boolean;
    layoutPermissionMode: LayoutPermissionMode;
    permission: Permission;
    context: ILayoutItemContext;
    component?: TComponent;
    componentProps?: ComponentProps<TComponent>;
    componentPosition?: PageComponentPosition;
};

const Page = <TComponent extends ComponentType<any>>({
    loading,
    permission,
    layoutPermissionMode,
    component,
    componentPosition = PageComponentPosition.BeforeChildren,
    componentProps,
    context,
    children,
}: PageProps<TComponent>) => {
    const Component = component;

    return (
        <>
            <SetPageLoading loading={loading} />
            <SetPagePermission
                permission={permission}
                permissionMode={layoutPermissionMode}
                context={context}
            />
            <Stack gap="sm" mb="xs">
                {!loading &&
                    Component &&
                    componentPosition === PageComponentPosition.BeforeChildren && (
                        <Component {...componentProps} />
                    )}
                {!loading && children}
                {!loading &&
                    Component &&
                    componentPosition === PageComponentPosition.AfterChildren && (
                        <Component {...componentProps} />
                    )}
            </Stack>
        </>
    );
};

export default Page;
